import classnames from 'classnames';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { NumberFormatValues } from 'react-number-format';

import { CardListSkeleton } from '@eika-kortbetjening/card-list-panel';
import { ErrorMessage, SuccessMessage, WarningMessage } from '@eika/attention-message';
import { ButtonTypes, LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { InfoCard } from '@eika/card';
import { CurrencyInput } from '@eika/currency-input';
import { Icon, IconTypes } from '@eika/icon';

import CardControlContext from '@/context/card-control/CardControlContext';
import { Format } from '@/domain/app';
import { useUserOver18 } from '@/domain/session';
import { useSetDebitCardSpendingLimits } from '@/react-query/hooks/mutation/use-set-spending-limits';
import { useCard } from '@/react-query/hooks/query/use-card';
import { useCardSpendingLimits, useDebitProductSpendingLimits } from '@/react-query/hooks/query/use-spending-limits';

import BackButton from '@components/back-button/BackButton';
import { prettyCategoryName } from '@components/card-control/functions';
import { SpendingLimitCategory } from '@components/card-control/types';
import FetchError from '@components/error/FetchError';
import LoaderWithText from '@components/loading/LoaderWithText';
import useText from '@hooks/use-text';

import { getAmountForCategory, getInvalidError, getSpendingLimitsWithUpdatedAmount } from './functions';

import './Form.scss';

interface FormProps {
    category: SpendingLimitCategory;
}

const Form = ({ category }: FormProps) => {
    const text = useText();
    const { data: card } = useCard();
    const { mutate, isPending, isError, isSuccess } = useSetDebitCardSpendingLimits();
    const { data: spendingLimits, isFetching: isLoadingGetUpdatedSpendingLimits } = useCardSpendingLimits();
    const { removeSelectedCategory } = useContext(CardControlContext);
    const {
        data: productSpendingLimits,
        isPending: isLoadingProductSpendingLimits,
        error: errorProductSpendingLimits,
    } = useDebitProductSpendingLimits(card);
    const userOver18 = useUserOver18();

    const initialValue = useMemo(() => getAmountForCategory(spendingLimits, category), [category, spendingLimits]);

    const [formLimit, setFormLimit] = useState<number | undefined>(() => initialValue);

    useEffect(() => {
        if (formLimit === undefined && initialValue !== undefined) {
            setFormLimit(initialValue);
        }
    }, [formLimit, initialValue]);

    const [formError, setFormError] = useState<string | undefined>(undefined);

    const onValueChange = (values?: NumberFormatValues) => {
        setFormLimit(values?.floatValue);
    };

    if (!spendingLimits || !card) {
        return null;
    }

    if (errorProductSpendingLimits !== null) {
        return <FetchError error={errorProductSpendingLimits} message={text.productSpendingLimitFetchError} />;
    }

    if (isLoadingProductSpendingLimits || typeof productSpendingLimits === 'undefined') {
        return <CardListSkeleton />;
    }

    // eslint-disable-next-line react/no-unstable-nested-components
    const ValidFormStatus = () => {
        if (formError) {
            return <WarningMessage text={formError} icon={IconTypes.INFO_24} />;
        }

        return null;
    };

    // eslint-disable-next-line react/no-unstable-nested-components
    const UpdateStatus = () => {
        if (isError) {
            return <ErrorMessage text={text.spendingLimitGenericError} icon={IconTypes.LUKK_24} />;
        }

        if (isSuccess) {
            return (
                <SuccessMessage
                    id={text.spendingLimitsSuccess}
                    text={text.spendingLimitsSuccess}
                    icon={IconTypes.CHECKMARK_LISTE_24}
                />
            );
        }

        return null;
    };

    const onSubmit = () => {
        if (!card) {
            return;
        }

        const invalidError = getInvalidError({
            amount: formLimit,
            card,
            category,
            productSpendingLimits,
            spendingLimits,
            text,
        });

        if (invalidError) {
            setFormError(invalidError);
            return;
        }

        if (!formLimit) {
            return;
        }

        const updatedSpendingLimits = getSpendingLimitsWithUpdatedAmount(spendingLimits, formLimit, category);

        mutate({
            cardId: card.id,
            limit: updatedSpendingLimits,
        });
    };

    if (isPending || isLoadingGetUpdatedSpendingLimits) {
        return <LoaderWithText title={text.loadingUpdateSpendingLimit} />;
    }

    if (card.allowedOperations.setSpendingLimit) {
        return (
            <div className="spending-limits__category__form">
                <CurrencyInput
                    name={category}
                    onValueChange={onValueChange}
                    value={formLimit}
                    label={prettyCategoryName(category, text)}
                    placeholder={text.spendingLimitsNoLimit}
                    autoComplete="off"
                    disabled={isPending || isLoadingGetUpdatedSpendingLimits}
                />
                <ValidFormStatus />
                <UpdateStatus />
                <div className="spending-limits__category__form__controls">
                    <BackButton onClick={() => removeSelectedCategory()} />
                    <ButtonWithSpinner
                        buttonType={ButtonTypes.STANDARD_POSITIVE}
                        onClick={() => onSubmit()}
                        showSpinner={isPending || isLoadingGetUpdatedSpendingLimits}
                    >
                        {text.formConfirm}
                    </ButtonWithSpinner>
                </div>
            </div>
        );
    }

    const displayValue =
        initialValue && initialValue > 0 ? Format.amount(initialValue, 0, true) : text.spendingLimitsNoLimit;

    return (
        <>
            <div className="spending-limits-amount__info-title">
                <div className="spending-limits-amount__info-title-content">{prettyCategoryName(category, text)}</div>
                <div
                    className={classnames('spending-limits-amount__info-title-content', 'font-scale-large', {
                        'font-scale-largest': initialValue && initialValue > 0,
                    })}
                >
                    {errorProductSpendingLimits ? text.spendingLimitFetchError : displayValue}
                </div>
            </div>
            {!userOver18 && (
                <InfoCard className="spending-limits-amount__info-card">
                    <div className="spending-limits-amount__info-text">
                        {text.spendingLimitsPleaseSpeakWithProtector}
                    </div>
                    <Icon type={IconTypes.LYSPAERE_36} />
                </InfoCard>
            )}
            <div className="spending-limits-amount__buttons">
                <LinkButton onClick={() => removeSelectedCategory()}>{text.formBack}</LinkButton>
            </div>
        </>
    );
};

export const DailyLimitForm = () => <Form category={SpendingLimitCategory.DailyLimit} />;

export const MonthlyLimitForm = () => <Form category={SpendingLimitCategory.MonthlyLimit} />;

export const DailyLimitEcommerceForm = () => <Form category={SpendingLimitCategory.DailyLimitEcommerce} />;

export const MonthlyLimitEcommerceForm = () => <Form category={SpendingLimitCategory.MonthlyLimitEcommerce} />;
